import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_LABELS = {
  ai_handled: "AI Handled", human_followup: "Human Follow-Up Needed", contacted: "Contacted", resolved: "Resolved",
};
const STATUS_BADGE = {
  ai_handled: "bg-ink/5 text-ink/60", human_followup: "bg-flame text-white",
  contacted: "bg-ink text-cream", resolved: "bg-ink/5 text-ink/40 line-through-none",
};
const METHOD_LABELS = { whatsapp: "WhatsApp", phone: "Phone", email: "Email" };
const REFERRAL_LABELS = { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", whatsapp: "WhatsApp", friend_family: "Friend or Family", google: "Google", other: "Other" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export function RoamieTab() {
  const [view, setView] = useState("overview");
  const views = [["overview", "Overview"], ["all", "Conversations"], ["leads", "Leads"], ["attention", "Needs Human Attention"], ["resolved", "Resolved"]];
  return (
    <div className="mt-6" data-testid="admin-roamie-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {views.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`roamie-view-${k}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${view === k ? "bg-ink text-cream border-ink" : "bg-white border-ink/15 hover:border-ink"}`}>
            {l}
          </button>
        ))}
      </div>
      {view === "overview" ? <RoamieOverview /> : <ConversationList view={view} />}
    </div>
  );
}

function RoamieOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/roamie/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!data) return <p className="text-ink/50 font-semibold" data-testid="roamie-overview-loading">Loading...</p>;
  const cards = [
    ["Total Conversations", data.total_conversations, "roamie-stat-total"],
    ["Leads", data.leads, "roamie-stat-leads"],
    ["Needs Human Attention", data.needs_attention, "roamie-stat-attention"],
    ["Contacted", data.contacted, "roamie-stat-contacted"],
    ["Resolved", data.resolved, "roamie-stat-resolved"],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(([label, value, tid]) => (
          <div key={tid} className="bg-white border border-black/10 p-5" data-testid={tid}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-ink/50">{label}</p>
            <p className="font-display text-4xl mt-2">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mt-8 mb-3">Recent Conversations</p>
      {data.recent.length === 0 ? (
        <EmptyState text="No Roamie conversations yet." testid="roamie-recent-empty" />
      ) : (
        <div className="space-y-2">{data.recent.map((c) => <ConversationRow key={c.conversation_id} c={c} />)}</div>
      )}
    </div>
  );
}

const EmptyState = ({ text, testid }) => (
  <div className="bg-white border border-dashed border-ink/20 p-10 text-center" data-testid={testid}>
    <p className="font-display text-xl uppercase text-ink/40">{text}</p>
    <p className="text-xs text-ink/40 mt-2">Real visitor activity will appear here automatically.</p>
  </div>
);

function ConversationList({ view }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const emptyTexts = {
    all: "No Roamie conversations yet.", leads: "No leads yet.",
    attention: "Nothing needs human attention right now.", resolved: "No resolved conversations yet.",
  };
  const load = (query = "") =>
    api.get(`/admin/roamie/conversations?view=${view}${query ? `&q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { setRows(null); load(); }, [view]);
  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-4 max-w-md">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, trip, message..." data-testid="roamie-search-input" className="rounded-none h-11 border-ink/20" />
        <Button type="submit" data-testid="roamie-search-btn" className="rounded-none bg-ink text-cream h-11 px-6 text-xs font-bold uppercase tracking-widest">Search</Button>
      </form>
      {rows === null ? <p className="text-ink/50 font-semibold">Loading...</p> :
        rows.length === 0 ? <EmptyState text={emptyTexts[view]} testid={`roamie-${view}-empty`} /> : (
          <div className="space-y-2" data-testid={`roamie-${view}-list`}>
            {rows.map((c) => <ConversationRow key={c.conversation_id} c={c} onChange={load} />)}
          </div>
        )}
    </div>
  );
}

function ConversationRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`roamie-conv-row-${c.conversation_id.slice(0, 8)}`}
        className="w-full text-left bg-white border border-black/10 p-4 hover:border-ink transition-colors flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm">{c.name || "Anonymous visitor"}</p>
            {c.is_lead && <span className="text-[10px] font-bold uppercase tracking-widest bg-flame text-white px-2 py-0.5">Lead</span>}
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${STATUS_BADGE[c.status] || "bg-ink/5 text-ink/60"}`}>{STATUS_LABELS[c.status] || c.status}</span>
          </div>
          <p className="text-xs text-ink/50 truncate mt-1">{c.preview || "—"}</p>
          <p className="text-[11px] text-ink/40 mt-1">
            {fmt(c.updated_at)} · {c.message_count} messages{c.trip_interest ? ` · Interested: ${c.trip_interest}` : ""}{c.source_page ? ` · from ${c.source_page}` : ""}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-ink/30 shrink-0" />
      </button>
      {open && <ConversationDialog cid={c.conversation_id} onClose={() => setOpen(false)} onChange={() => onChange && onChange()} />}
    </>
  );
}

function ConversationDialog({ cid, onClose, onChange }) {
  const [conv, setConv] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/roamie/conversations/${cid}`).then((r) => {
      setConv(r.data); setStatus(r.data.status || "ai_handled"); setNotes(r.data.notes || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, [cid]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/roamie/conversations/${cid}`, { status, notes });
      toast.success("Conversation updated.");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto rounded-none" data-testid="roamie-conv-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">
            {conv?.name || "Anonymous visitor"} {conv?.is_lead && <span className="text-flame text-sm align-middle ml-2">LEAD</span>}
          </DialogTitle>
        </DialogHeader>
        {!conv ? <p className="text-ink/50 font-semibold">Loading...</p> : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <Meta label="Conversation ID" value={conv.conversation_id.slice(0, 8)} mono />
              <Meta label="Started" value={fmt(conv.created_at)} />
              <Meta label="Source page" value={conv.source_page || "—"} />
              <Meta label="Trip interest" value={conv.trip_interest || "—"} />
              <Meta label="Preferred contact" value={METHOD_LABELS[conv.contact_method] || "—"} />
              <Meta label="Heard via" value={REFERRAL_LABELS[conv.referral_source] || "—"} />
              {conv.email && <Meta label="Email" value={conv.email} />}
              {conv.phone && <Meta label="Phone / WhatsApp" value={conv.phone} />}
              {conv.question && <div className="col-span-2 sm:col-span-3"><Meta label="Follow-up question" value={conv.question} /></div>}
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-ink/50 mb-2">Transcript (private)</p>
              <div className="bg-cream border border-ink/10 p-4 space-y-3 max-h-72 overflow-y-auto" data-testid="roamie-transcript">
                {(conv.messages || []).map((m, i) => (
                  <div key={i} className={m.role === "user" ? "text-right" : ""}>
                    <span className={`inline-block px-3 py-2 text-xs max-w-[85%] text-left whitespace-pre-wrap ${m.role === "user" ? "bg-flame text-white" : "bg-white border border-ink/15"}`}>
                      {m.text.replace("[HANDOFF]", "").trim()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-ink/50 mb-2">Status</p>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="rounded-none" data-testid="roamie-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`roamie-status-${v}`}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-ink/50 mb-2">Internal notes (private)</p>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="roamie-notes-input" className="rounded-none" placeholder="Staff notes..." />
              </div>
            </div>
            <Button onClick={save} disabled={saving} data-testid="roamie-conv-save-btn"
              className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
              {saving ? "Saving..." : "Save Status & Notes"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Meta = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-widest text-ink/40">{label}</p>
    <p className={`font-semibold mt-0.5 break-all ${mono ? "font-mono" : ""}`}>{value}</p>
  </div>
);
